import { useMemo } from 'react';
import { createStaticDataProvider } from '@adv-frontend/data-provider';
import { createFilterSelectWithSuggest, HighlightedText } from '@adv-frontend/filters';
import { type OpenApiSchemaCategory } from '#shared/api/schema';
import { SelectItemWithLevel } from '#shared/components/SelectItemWithLevel';
import { type CategoryWithParentsPath, enrichCategoriesWithParentPath } from '#shared/lib/category';
import { trimAndLowerString } from '#shared/lib/string';
import { useCategoryGetAll } from '../../api';
import { useFilterTitleByValue } from '../../hooks/useFilterTitleByValue';

export const CATEGORY_FILTER_ID = 'category' as const;

export const CATEGORY_FILTER_TITLE = 'Категория';

export const createCategoryFilter = () =>
    createFilterSelectWithSuggest<typeof CATEGORY_FILTER_ID, CategoryWithParentsPath<OpenApiSchemaCategory>>({
        id: CATEGORY_FILTER_ID,
        title: CATEGORY_FILTER_TITLE,
        suggestProps: {
            dataProvider: function Component(props) {
                const searchString = trimAndLowerString(props.queryArg.searchValue);

                const { data: categories } = useCategoryGetAll({});

                const filteredCategories = useMemo(
                    () =>
                        enrichCategoriesWithParentPath(categories ?? []).filter((category) =>
                            trimAndLowerString(category.title).includes(searchString)
                        ),
                    [categories, searchString]
                );

                const dataProvider = useMemo(() => createStaticDataProvider(filteredCategories), [filteredCategories]);

                return props.children(dataProvider);
            },
            useTitleByValue: useFilterTitleByValue,
            renderSelectListItem: ({ item, search }) => {
                if (search.length > 0) {
                    return <HighlightedText text={item.parentsPath} search={search} joinParts />;
                }

                return (
                    <SelectItemWithLevel
                        id={item.id}
                        level={item.level}
                        title={<HighlightedText text={item.title} search={search} joinParts />}
                    />
                );
            },
            renderFilterChipsItem: ({ item }) => item.parentsPath,
        },
    });
